
import Label from '../../../../../ui/label';
import { useDispatch } from 'react-redux';
import { addRowInputLevelRanking, removeLevelRankingRiderByIndex, updateInputLevelRanking } from '../../../../../../features/gms-proposal-forms/coverage/level-ranking/coverage-type-level-ranking.slice';
import { formatAmountType } from '../../../../../../utils/format-numbers.utils';
import { MaterialSymbolsDeleteRounded } from '../../../../../../assets/icon/iconify';
import Input from '../../../../../ui/input';
import { customStyle, defaultStyle } from '../../../../../../constant/custom-style';
import { useProposalTypeData } from '../../../../../../hooks/proposal/use-proposal-type-data';

export default function LevelRanking() {
    const dispatch = useDispatch();
    const { coverage, inputsByLevelRanking } = useProposalTypeData();

    const addInputLevelRanking = () => dispatch(addRowInputLevelRanking());

    const removeLastInputLevelRanking = (id?: string) => {
        if (!id) return;
        dispatch(removeLevelRankingRiderByIndex(id));
    };

    const handleFAmountChange = (id: string, value: string) => {
        const onlyDigits = value.replace(/\D/g, '');
        const numericValue = onlyDigits === '' ? 0 : Number(onlyDigits);
        dispatch(updateInputLevelRanking({ id, fAmount: numericValue }));
    };

    const handleDesignationChange = (id: string, value: string) => {
        dispatch(updateInputLevelRanking({ id, label: value }));
    };

    const getInputClass = (value: string | number) =>
        `${defaultStyle} w-full min-w-0 ${value !== '' && value !== 0 ? customStyle : ''}`;

    const canRemoveRow = inputsByLevelRanking.length > 2;
    const maxRowsReached = inputsByLevelRanking.length >= 10;

    return (
        <div className="my-2 min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            {/* HEADER */}
            <div className="flex flex-row items-center justify-between gap-2 border-b border-[#186cb2]/20 bg-[#186cb2]/10 px-3 py-3 sm:px-4">
                <div className="min-w-0 flex-1">
                    <Label
                        label={coverage?.name ?? ''}
                        fontWeight="font-semibold text-gray-800"
                        fontSize="text-xs"
                    />
                    <p className="mt-1 hidden text-[10px] leading-4 text-gray-500 sm:block sm:text-[11px]">
                        Enter the designation and insurance coverage amount for each level.
                    </p>
                    <p className="mt-1 text-[10px] text-gray-400">
                        {inputsByLevelRanking.length} of 10 rows
                    </p>
                </div>

                <button
                    type="button"
                    onClick={addInputLevelRanking}
                    disabled={maxRowsReached}
                    aria-label="Add row"
                    title="Add row"
                    className="inline-flex size-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-[#186cb2] bg-[#186cb2] text-xs font-medium text-white transition-colors hover:bg-[#145d99] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-4"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14" />
                        <path d="M5 12h14" />
                    </svg>
                    <span className="hidden sm:inline">Add row</span>
                </button>
            </div>

            {/* RESPONSIVE TABLE */}
            <div className="min-w-0">
                <div className="overflow-x-auto overscroll-x-contain">
                    <table className="w-full min-w-[600px] border-collapse text-xs sm:min-w-[680px] lg:min-w-0 lg:table-fixed">
                        <thead>
                            <tr className="bg-gray-100 text-gray-800">
                                <th className="w-14 border px-2 py-2.5 text-center text-[10px] font-semibold sm:text-xs lg:w-[10%]">
                                    #
                                </th>
                                <th className="w-[42%] border px-2 py-2.5 text-left text-[10px] font-semibold sm:px-3 sm:text-xs lg:w-[42%]">
                                    Designation
                                </th>
                                <th className="w-[42%] border px-2 py-2.5 text-left text-[10px] font-semibold sm:px-3 sm:text-xs lg:w-[38%]">
                                    Insurance Coverage Amount
                                </th>
                                <th className="w-14 border px-1 py-2.5 text-center text-[10px] font-semibold sm:w-16 sm:px-2 sm:text-xs lg:w-[10%]">
                                    Action
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {inputsByLevelRanking.map((row, idx) => (
                                <tr key={row.id} className="transition-colors hover:bg-gray-50">
                                    <td className="border px-2 py-2 text-center text-xs font-medium text-gray-500">
                                        {idx + 1}
                                    </td>

                                    <td className="border px-2 py-2 sm:px-3">
                                        <Input
                                            type="text"
                                            name={`designation-${row.id}`}
                                            placeholder={`Enter designation ${idx + 1}`}
                                            isCustomStyle={true}
                                            customCss={getInputClass(row.label)}
                                            value={row.label}
                                            onChange={(e) =>
                                                handleDesignationChange(row.id, e.target.value)
                                            }
                                            required={true}
                                        />
                                    </td>

                                    <td className="border px-2 py-2 sm:px-3">
                                        <Input
                                            type="text"
                                            name={`coverageAmount-${row.id}`}
                                            inputMode="numeric"
                                            placeholder="Enter amount"
                                            isCustomStyle={true}
                                            customCss={getInputClass(row.fAmount)}
                                            value={formatAmountType(row.fAmount)}
                                            onChange={(e) =>
                                                handleFAmountChange(row.id, e.target.value)
                                            }
                                            required={true}
                                        />
                                    </td>

                                    <td className="border px-1 py-2 text-center sm:px-2">
                                        <button
                                            type="button"
                                            onClick={() => removeLastInputLevelRanking(row.id)}
                                            disabled={!canRemoveRow}
                                            aria-label={`Remove level ${idx + 1}`}
                                            title="Remove row"
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-500 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 sm:h-9 sm:w-9"
                                        >
                                            <MaterialSymbolsDeleteRounded
                                                color="#ef4444"
                                                height={20}
                                                width={20}
                                            />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-1 border-t border-gray-100 bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[10px] text-gray-500">
                        {inputsByLevelRanking.length} of 10 rows
                    </p>
                    <p className="text-[10px] text-gray-400">
                        Swipe horizontally to view all columns on smaller screens.
                    </p>
                </div>
            </div>
        </div>
    );
}
